import { formatDateTime } from '@localstack/services';
import { IdentityEventScope, IdentityLogEvent } from '@localstack/types';
import { DataGrid, GridColDef } from '@localstack/ui';
import { Chip, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { GridEventListener } from '@mui/x-data-grid';
import { ReactElement } from 'react';

export type EventLogRow = IdentityLogEvent & { id: string };

const hasLogDetails = (row: IdentityLogEvent): boolean =>
  !!row.idp_name || (!!row.extras && Object.keys(row.extras).length > 0);

const COLUMNS: GridColDef[] = [
  {
    field: 'created_at',
    headerName: 'Time',
    width: 160,
    renderCell: ({ row }) => <span>{row.created_at ? formatDateTime(row.created_at) : '—'}</span>,
  },
  {
    field: 'message',
    headerName: 'Message',
    flex: 3,
    renderCell: ({ row }) => (
      <Tooltip title={row.message} placement="bottom-start">
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
          {row.message}
        </span>
      </Tooltip>
    ),
  },
  {
    field: 'scope',
    headerName: 'Scope',
    width: 120,
    renderCell: ({ row }) => (
      <Chip label={row.scope} size="small" color={row.scope === IdentityEventScope.SSO ? 'primary' : 'default'} />
    ),
  },
  {
    field: 'success',
    headerName: 'Status',
    width: 100,
    renderCell: ({ row }) => (
      <Chip label={row.success ? 'Success' : 'Failed'} size="small" color={row.success ? 'success' : 'error'} />
    ),
  },
  {
    field: 'user_email',
    headerName: 'User',
    flex: 1,
  },
  {
    field: 'details',
    headerName: '',
    width: 48,
    sortable: false,
    renderCell: ({ row }) =>
      hasLogDetails(row) ? (
        <Tooltip title="Click to see more details">
          <InfoOutlined fontSize="small" sx={{ color: 'text.primary' }} />
        </Tooltip>
      ) : null,
  },
];

type EventLogTableProps = {
  rows: EventLogRow[];
  loading?: boolean;
  onRowClick: GridEventListener<'rowClick'>;
};

export const EventLogTable = ({ rows, loading, onRowClick }: EventLogTableProps): ReactElement => (
  <DataGrid
    autoHeight
    columns={COLUMNS}
    rows={rows}
    loading={loading}
    density="compact"
    disableColumnMenu
    hideFooter
    onRowClick={onRowClick}
    getRowClassName={(params) => (hasLogDetails(params.row) ? 'has-details' : '')}
    sx={{
      '& .MuiDataGrid-cell:focus': { outline: 'none' },
      '& .MuiDataGrid-row:hover': { cursor: 'default' },
      '& .MuiDataGrid-row.has-details:hover': { cursor: 'pointer' },
    }}
  />
);
